import { logofaded, coverImage } from "../../assets/images/images";
import Footer from "../../components/Footer/Footer";
import Topbar from "../../components/Topbar/Topbar";
import "./login.css";

import React, { useState } from "react";
import API from "../../API";
import ClipLoader from "react-spinners/ClipLoader";
import { useTranslation } from 'react-i18next';
import {
  FaEnvelope, FaLock, FaEye, FaEyeSlash, FaSignInAlt, FaArrowLeft,
  FaExclamationCircle, FaPlaneDeparture, FaHotel, FaPassport, FaTicketAlt
} from "react-icons/fa";

export default function Login() {
  const { t, i18n } = useTranslation();
  const changeLanguage = (lng) => {
    if (i18n.language === lng) return;
    i18n.changeLanguage(lng);
    localStorage.setItem('language', lng);
    window.location.reload();
  };
  const isFr = i18n.language === 'fr';
  const isAr = i18n.language === 'ar';
  const isRTL = isAr;
  const [seepsw, setSeepsw] = useState(false);
  const [formData, setFormData] = useState(() => {
    // Pre-fill the email if the user previously ticked "Remember me"
    let remembered = '';
    try { remembered = localStorage.getItem('rememberedEmail') || ''; } catch (_) {}
    return { email: remembered, password: '' };
  });
  const [rememberMe, setRememberMe] = useState(() => {
    try { return !!localStorage.getItem('rememberedEmail'); } catch (_) { return false; }
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    setErrorMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setLoading(true);

    if (formData.password.length < 8) {
      setErrorMessage(t("login.password_error"));
      setLoading(false);
      return;
    }

    try {
      const response = await API.post('/auth/login/user', formData);
      if (response.status === 201) {
        const { token, firstname, lastname, email, id } = response.data;
        localStorage.setItem("protoken", token);
        localStorage.setItem("fullname", `${firstname} ${lastname}`);
        localStorage.setItem("email", email);
        localStorage.setItem("userId", id);

        // Remember-me — persist / clear the remembered email (never password)
        try {
          if (rememberMe) {
            localStorage.setItem('rememberedEmail', email || formData.email);
          } else {
            localStorage.removeItem('rememberedEmail');
          }
        } catch (_) { /* ignore */ }

        // Honour ?next=... so users return to the page they came from
        const params = new URLSearchParams(window.location.search);
        const nextRaw = params.get('next');
        let target = '/';
        if (nextRaw) {
          try {
            const decoded = decodeURIComponent(nextRaw);
            // Only allow internal paths (prevent open-redirect)
            if (decoded.startsWith('/') && !decoded.startsWith('//')) {
              target = decoded;
            }
          } catch (e) { /* keep default */ }
        }
        window.location.href = target;
      }
    } catch (error) {
      console.error("Login error:", error);
      if (error.response) {
        setErrorMessage(error.response.data.message || t("login.login_error"));
      } else {
        setErrorMessage(t("login.login_error"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="Page_login" dir={isRTL ? "rtl" : "ltr"}>
      <Topbar />

      <div className="auth-langbar" role="group" aria-label="language">
        <button type="button" className={`auth-langbtn ${isFr ? 'active' : ''}`} onClick={() => changeLanguage('fr')} aria-pressed={isFr}>FR</button>
        <button type="button" className={`auth-langbtn ${isAr ? 'active' : ''}`} onClick={() => changeLanguage('ar')} aria-pressed={isAr}>AR</button>
      </div>

      <main className="auth-shell">
        {/* Brand side */}
        <aside
          className="auth-brand"
          style={{ backgroundImage: `url(${coverImage})` }}
        >
          <div className="auth-brand__scrim" />
          <div className="auth-brand__content">
            <div className="auth-brand__row">
              <div className="auth-brand__logo">
                <img src={logofaded} alt="Protour" />
              </div>
              <h2 className="auth-brand__heading">{t("login.brand_heading")}</h2>
            </div>
            <p className="auth-brand__sub">{t("login.brand_sub")}</p>

            <ul className="auth-features">
              <li>
                <span className="auth-features__ico"><FaHotel /></span>
                <div>
                  <strong>{t("login.feature_hotels_title")}</strong>
                  <span>{t("login.feature_hotels_sub")}</span>
                </div>
              </li>
              <li>
                <span className="auth-features__ico"><FaPlaneDeparture /></span>
                <div>
                  <strong>{t("login.feature_voyages_title")}</strong>
                  <span>{t("login.feature_voyages_sub")}</span>
                </div>
              </li>
              <li>
                <span className="auth-features__ico"><FaPassport /></span>
                <div>
                  <strong>{t("login.feature_visa_title")}</strong>
                  <span>{t("login.feature_visa_sub")}</span>
                </div>
              </li>
              <li>
                <span className="auth-features__ico"><FaTicketAlt /></span>
                <div>
                  <strong>{t("login.feature_reservations_title")}</strong>
                  <span>{t("login.feature_reservations_sub")}</span>
                </div>
              </li>
            </ul>
          </div>
        </aside>

        {/* Form side */}
        <section className="auth-form-wrap">
          <div className="auth-card">
            <div className="auth-card__head">
              <span className="auth-pill"><span className="auth-pill__dot"><FaSignInAlt /></span> {t("login.pill")}</span>
              <h1 className="auth-card__title">{t("login.title")}</h1>
              <p className="auth-card__sub">{t("login.card_sub")}</p>
            </div>

            <form className="auth-form" onSubmit={handleSubmit}>
              <div className="auth-field">
                <label className="auth-label">
                  <FaEnvelope className="auth-label__ico" />
                  <span>{t("login.email_label")}</span>
                </label>
                <input
                  className="auth-input"
                  placeholder={t("login.email_placeholder")}
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="auth-field">
                <label className="auth-label">
                  <FaLock className="auth-label__ico" />
                  <span>{t("login.password_label")}</span>
                </label>
                <div className="auth-input-group">
                  <input
                    className="auth-input"
                    placeholder={t("login.password_placeholder")}
                    type={seepsw ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                  />
                  <button
                    type="button"
                    className="auth-input-action"
                    onClick={() => setSeepsw((s) => !s)}
                    aria-label="toggle password"
                  >
                    {seepsw ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <div className="auth-row">
                <label className="auth-check">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <span className="auth-check__box" />
                  <span>{t("login.remember_me")}</span>
                </label>
                <a className="auth-link" href="/forgot-password">
                  {t("login.forgot_password")}
                </a>
              </div>

              {errorMessage && (
                <div className="auth-banner auth-banner--err">
                  <FaExclamationCircle />
                  <p>{errorMessage}</p>
                </div>
              )}

              <button type="submit" className="auth-btn auth-btn--primary" disabled={loading}>
                {loading ? (
                  <ClipLoader color={"#fff"} loading={loading} size={18} />
                ) : (
                  <>
                    <FaSignInAlt /> {t("login.submit_button")}
                  </>
                )}
              </button>
            </form>

            <div className="auth-divider"><span>{t("login.or")}</span></div>

            <p className="auth-foot">
              {t("login.no_account")} <a className="auth-link auth-link--strong" href="/register">{t("login.create_account")}</a>
            </p>
            <a href="/" className="auth-home">
              <FaArrowLeft /> {t("login.return_home")}
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
